'use client';

import React, { useState } from 'react';

import Icon from '@/components/ui/AppIcon';

export default function Footer() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'owner',
  });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 4000);
    setFormData({ name: '', email: '', phone: '', role: 'owner' });
  };

  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-primary text-primary-foreground">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 pb-10 border-b border-white/10">
          {/* Brand Column */}
          <div className="lg:col-span-4">
            <div className="flex items-center gap-2.5 mb-4">
              <div className="w-9 h-9 rounded-lg bg-accent flex items-center justify-center flex-shrink-0">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10 2L18 8V18H12V13H8V18H2V8L10 2Z" fill="#1B4332" />
                </svg>
              </div>
              <div className="flex flex-col leading-none">
                <span className="font-bold text-sm tracking-tight text-white">Empire Venture</span>
                <span className="text-[10px] font-mono tracking-widest uppercase text-white/60">Nepal</span>
              </div>
            </div>
            <p className="text-sm text-white/65 leading-relaxed mb-5 max-w-xs">
              Professional property management in Kathmandu. Helping owners and tenants with transparent, reliable service since day one.
            </p>
            <div className="space-y-2">
              <a
                href="tel:9851075502"
                className="flex items-center gap-2 text-sm text-white/75 hover:text-white transition-colors group"
              >
                <Icon name="PhoneIcon" size={15} className="text-accent group-hover:scale-110 transition-transform" />
                <span>Bijay Krishna: 9851075502</span>
              </a>
              <a
                href="tel:9841273772"
                className="flex items-center gap-2 text-sm text-white/75 hover:text-white transition-colors group"
              >
                <Icon name="PhoneIcon" size={15} className="text-accent group-hover:scale-110 transition-transform" />
                <span>Bikash Krishna: 9841273772</span>
              </a>
              <div className="flex items-center gap-2 text-sm text-white/75">
                <Icon name="GlobeAltIcon" size={15} className="text-accent" />
                <span>empireventure.com.np</span>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div className="lg:col-span-2">
            <h4 className="text-xs font-mono font-medium tracking-widest uppercase text-white/40 mb-4">Quick Links</h4>
            <ul className="space-y-2.5">
              {[
                { label: 'Properties', href: '#properties' },
                { label: 'Services', href: '#services' },
                { label: 'About Us', href: '#about' },
                { label: 'Our Team', href: '#team' },
                { label: 'FAQ', href: '#faq' },
              ].map((link) => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    className="text-sm text-white/65 hover:text-white transition-colors"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div className="lg:col-span-2">
            <h4 className="text-xs font-mono font-medium tracking-widest uppercase text-white/40 mb-4">Services</h4>
            <ul className="space-y-2.5">
              {[
                'Rent Management',
                'Tenant Management',
                'Maintenance',
                'Owner Services',
                'Property Reports',
              ].map((s) => (
                <li key={s}>
                  <a href="#services" className="text-sm text-white/65 hover:text-white transition-colors">
                    {s}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Service Areas */}
          <div className="lg:col-span-2">
            <h4 className="text-xs font-mono font-medium tracking-widest uppercase text-white/40 mb-4">Service Areas</h4>
            <ul className="space-y-2.5">
              {['New Baneshwor', 'Koteswor', 'New Road', 'Lazimpat', 'Putalisadak', 'Hattisar', 'Naxal'].map((area) => (
                <li key={area}>
                  <span className="text-sm text-white/65">{area}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Contact Form */}
          <div className="lg:col-span-2">
            <h4 className="text-xs font-mono font-medium tracking-widest uppercase text-white/40 mb-4">Quick Inquiry</h4>
            {submitted ? (
              <div className="flex items-center gap-2 text-sm text-accent py-2">
                <Icon name="CheckCircleIcon" size={16} />
                <span>Inquiry sent!</span>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-3">
                <input
                  type="text"
                  placeholder="Your Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="w-full px-3 py-2.5 text-sm bg-white/10 border border-white/15 rounded-md text-white placeholder-white/40 focus:outline-none focus:border-accent transition-colors"
                />
                <input
                  type="email"
                  placeholder="Email Address"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="w-full px-3 py-2.5 text-sm bg-white/10 border border-white/15 rounded-md text-white placeholder-white/40 focus:outline-none focus:border-accent transition-colors"
                />
                <input
                  type="tel"
                  placeholder="Phone Number"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-3 py-2.5 text-sm bg-white/10 border border-white/15 rounded-md text-white placeholder-white/40 focus:outline-none focus:border-accent transition-colors"
                />
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="w-full px-3 py-2.5 text-sm bg-white/10 border border-white/15 rounded-md text-white focus:outline-none focus:border-accent transition-colors appearance-none"
                >
                  <option value="owner" className="bg-primary text-white">Property Owner</option>
                  <option value="tenant" className="bg-primary text-white">Tenant</option>
                </select>
                <button
                  type="submit"
                  className="w-full py-2.5 text-sm font-semibold bg-accent text-foreground rounded-md hover:bg-accent/90 transition-all"
                >
                  Submit Inquiry
                </button>
              </form>
            )}
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-6 flex flex-col sm:flex-row justify-between items-center gap-3">
          <p className="text-xs text-white/40 font-mono">
            © {currentYear} Empire Venture Nepal. All rights reserved.
          </p>
          <div className="flex items-center gap-5">
            <a href="#privacy" className="text-xs text-white/40 hover:text-white/70 transition-colors">
              Privacy Policy
            </a>
            <a href="#terms" className="text-xs text-white/40 hover:text-white/70 transition-colors">
              Terms & Conditions
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}